import React from 'react';
import PrintDocument, { DocSection, DocFieldGrid, DocField, formatDate } from '../PrintDocument';
import { sectionLabel, personName, formatRegNo } from '../labels';
import { formatPhone } from '../phone';
import './documents.css';

/**
 * StudentDossierDoc — إضبارة طالب.
 *
 * The registration form printed when the office opens a pupil's dossier
 * (شؤون الطلاب → إضبارة جديدة, or later from «الإضبارات»): every fact
 * taken at the counter, on one A4 sheet, for the pupil's paper file.
 * No signature lines and no registrar name: the administration signs and
 * stamps wherever there is room. Fees, notes and the family status stay
 * off the paper too (owner, 2026-10-18).
 *
 * Different job from StudentRecordDoc (وثيقة طالب): that one is an
 * attestation handed to the parent; this one is the record itself.
 *
 * PROPS CONTRACT
 *   student   object REQUIRED — `api.student(id)`: every students column
 *             plus section_label, grade_title, bus_number, created_by_name.
 *   family    object? — `api.family(student.family_id)` (family_summaries).
 *   academicYear string? — "2026–2027", printed in the header box.
 *   schoolName / logoUrl — the letterhead (app/about.js SCHOOL_INFO).
 *   showToolbar / onClose / autoPrint — forwarded to PrintDocument.
 *
 * PURE / PRESENTATIONAL: fetches nothing. See pages/dossier/DossierPrint.js.
 */
const GENDER = { male: 'ذكر', female: 'أنثى' };

/** The usual papers (dossierModel ID_DOCS / SCHOOL_DOCS), ticked when handed in. */
const ID_PAPERS = ['هوية', 'دفتر عائلة', 'إخراج قيد', 'بيان عائلي', 'جواز سفر', 'شهادة ولادة'];
const SCHOOL_PAPERS = ['جلاء', 'وثيقة طالب', 'شهادة تاسع', 'إفادة إنهاء', 'صورة طبق الأصل', 'تسلسل دراسي'];
const tokens = (v) => String(v || '').split(/[,،+\n·]/).map((t) => t.trim()).filter(Boolean);

function PaperList({ label, list, have }) {
  const extra = have.filter((t) => !list.includes(t));
  return (
    <div className="ui-docx-papers">
      <span className="ui-docx-papers-label">{label}</span>
      {[...list, ...extra].map((t) => (
        <span key={t} className={`ui-docx-paper${have.includes(t) ? ' is-on' : ''}`}>
          <span className="ui-docx-paper-box" aria-hidden="true">{have.includes(t) ? '✓' : ''}</span>
          {t}
        </span>
      ))}
    </div>
  );
}

/** Phones print as "+963 933 123 456" — isolated LTR by DocField numeric. */
const phone = (v) => (v ? formatPhone(v) : null);

export default function StudentDossierDoc({
  student,
  family,
  academicYear,
  schoolName,
  logoUrl,
  showToolbar = true,
  onClose,
  autoPrint = false,
}) {
  if (!student) return null;
  const fam = family || {};
  const name = personName(student);
  const section = student.section_label || student.section_id ? sectionLabel(student) : null;

  return (
    <PrintDocument
      title="إضبارة طالب"
      docNumber={formatRegNo(student.registration_number) || undefined}
      schoolName={schoolName}
      schoolSubtitle="شؤون الطلاب"
      logoUrl={logoUrl}
      showToolbar={showToolbar}
      onClose={onClose}
      autoPrint={autoPrint}
      footerNote="تُحفظ هذه الإضبارة في ملف الطالب لدى إدارة المدرسة، ويُعتمد بها بعد التوقيع والختم."
    >
      <div className="ui-docx-dossier">
      <div className="ui-docx-dossier-top">
        <div className="ui-docx-dossier-id">
          <div className="ui-docx-dossier-name">{name}</div>
          <div className="ui-docx-dossier-line">
            {section || 'لم تُحدَّد الشعبة بعد'}
            {academicYear ? <>{'  ·  '}العام الدراسي <span className="ui-doc-num">{academicYear}</span></> : null}
          </div>
          <div className="ui-docx-dossier-line">
            رقم التسجيل: <span className="ui-doc-num">{formatRegNo(student.registration_number) || '—'}</span>
            {'  ·  '}
            تاريخ التسجيل: <span className="ui-doc-num">{formatDate(student.registration_date || student.created_at) || '—'}</span>
          </div>
        </div>
        {student.picture_url
          ? <img className="ui-docx-photo" src={student.picture_url} alt="" />
          : <div className="ui-docx-photo" aria-hidden="true">صورة الطالب</div>}
      </div>

      <DocSection title="بيانات الطالب">
        <DocFieldGrid columns={3}>
          <DocField label="الاسم" value={student.first_name} />
          <DocField label="اسم الأب" value={fam.father_name} />
          <DocField label="الكنية" value={student.last_name || fam.last_name} />
          <DocField label="اسم الجد" value={student.grandfather_name} />
          <DocField label="الجنس" value={GENDER[student.gender]} />
          <DocField label="تاريخ الميلاد" value={formatDate(student.birth_date)} numeric />
          <DocField label="مكان الولادة" value={student.birth_place} />
          <DocField label="الرقم الوطني" value={student.national_id} numeric />
          <DocField label="هاتف الطالب" value={phone(student.student_phone)} numeric />
        </DocFieldGrid>
      </DocSection>

      <DocSection title="القيد المدني">
        <DocFieldGrid columns={3}>
          <DocField label="محل القيد" value={student.registry_place} />
          <DocField label="رقم القيد" value={student.registry_number} numeric />
          <DocField label="الأمانة" value={fam.amana} />
          <DocField label="وافد من" value={student.origin_place} />
          <DocField label="الوضع الصحي" value={student.health_status} />
        </DocFieldGrid>
      </DocSection>

      <DocSection title="الأوراق الثبوتية">
        <PaperList label="المدنية" list={ID_PAPERS} have={tokens(student.id_document_type)} />
        <PaperList label="الدراسية" list={SCHOOL_PAPERS} have={tokens(student.displacement_status)} />
      </DocSection>

      <DocSection title="بيانات الأسرة">
        <DocFieldGrid columns={3}>
          <DocField label="اسم الأب" value={fam.father_name} />
          <DocField label="عمل الأب" value={fam.father_job} />
          <DocField label="هاتف الأب" value={phone(fam.father_phone)} numeric />
          <DocField label="اسم الأم" value={fam.mother_name} />
          <DocField label="كنية الأم" value={fam.mother_last_name} />
          <DocField label="هاتف الأم" value={phone(fam.mother_phone)} numeric />
          <DocField label="العنوان" value={fam.address} wide />
        </DocFieldGrid>
      </DocSection>

      <DocSection title="التسجيل الدراسي">
        <DocFieldGrid columns={3}>
          <DocField label="الصف" value={student.grade_title} />
          <DocField label="الشعبة" value={section} />
          <DocField label="الباص" value={student.bus_number ? `${student.bus_number}${student.driver_name ? ` — ${student.driver_name}` : ''}` : 'بدون مواصلات'} />
        </DocFieldGrid>
      </DocSection>
      </div>
    </PrintDocument>
  );
}
